import React, { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";

const CASE_STUDIES = [
  {
    id: "aptia",
    client: "Aptia",
    category: "Enterprise platform & transformation",
    image: "/images/aptia-case-study.png",
    imageBg: "bg-[#0A2533]",
    desc: "We helped Aptia modernize its technology platforms and process with AI, delivering greater efficiency, scalability and customer value across the business.",
    metrics: [
      { value: "40%", label: "Faster Process" },
      { value: "30%", label: "Cost Reduction" },
      { value: "2x", label: "Customer Satisfaction" },
    ],
  },
  {
    id: "skipper",
    client: "Skipper Ltd.",
    category: "Digitizing manufacturing Operations",
    image: "/images/skipper-case-study.jpg",
    imageBg: "bg-[#114C77]",
    desc: "We built digital solutions to streamline galvanizing and tower manufacturing processes, enabling better visibility and efficiency.",
    metrics: [
      { value: "25%", label: "Reduction in Downtime" },
      { value: "18%", label: "Increase in Production" },
      { value: "30%", label: "Improved Operations" },
    ],
  },
  {
    id: "retail",
    client: "Leading Retail Chain",
    category: "Personalized Customer experiences",
    image: "/images/retail-case-study.jpg",
    imageBg: "bg-slate-900",
    desc: "We implement AI-driven analytics to understand customer behavior, personalize engagement and optimize inventory.",
    metrics: [
      { value: "40%", label: "Faster Process" },
      { value: "30%", label: "Cost Reduction" },
      { value: "2x", label: "Customer Satisfaction" },
    ],
  },
];

export const AiInActionSection = () => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);

  const scrollSlider = (direction) => {
    if (sliderRef.current) {
      const scrollAmount = direction === "left" ? -350 : 350;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
    }
  };

  return (
    <section 
      className="relative w-full py-16 lg:py-24 overflow-hidden text-[#0F172A] border-t border-[#E2E8F0]/60"
      style={{ backgroundColor: "rgba(242, 248, 252, 1)" }}
    >
      <div className="mx-auto max-w-[1440px] px-6 lg:px-[70px]">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-start">
          
          {/* Left Header Column */}
          <div className="lg:col-span-4 flex flex-col justify-between pt-2">
            <div className="flex flex-col gap-3">
              <p className="text-[11px] font-bold uppercase tracking-[2.5px] text-[#64748B]">
                A CLOSER LOOK
              </p>
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] font-bold leading-[1.15] text-[#0B132B]">
                AI in action: Real<br className="hidden sm:inline" /> solutions, real results
              </h2>
              <p className="text-[15px] sm:text-[16px] leading-[26px] text-[#475569] mt-1 max-w-[340px]">
                See how we've helped businesses across industries solve complex challenges with AI.
              </p>
            </div>

            {/* View Case Studies Link with Exact Figma Gradient & Typography */}
            <div className="mt-8">
              <button
                type="button"
                onClick={() => {
                  window.scrollTo(0, 0);
                  navigate("/case-studies");
                }}
                className="inline-flex items-center gap-2 group cursor-pointer transition-transform duration-200 hover:translate-x-0.5"
              >
                <span
                  className="font-semibold text-[14px] leading-[20px] bg-clip-text text-transparent"
                  style={{
                    fontFamily: "'Inter', sans-serif",
                    fontWeight: 600,
                    fontSize: "14px",
                    lineHeight: "20px",
                    letterSpacing: "0px",
                    backgroundImage: "linear-gradient(90deg, rgba(5, 214, 218, 1) 0%, rgba(49, 112, 247, 1) 100%)",
                  }}
                >
                  View Case Studies
                </span>
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  strokeWidth={2.4}
                  style={{ color: "rgba(49, 112, 247, 1)" }}
                />
              </button>
            </div>
          </div>

          {/* Right Cards Slider Column */}
          <div className="lg:col-span-8 flex flex-col min-w-0">
            {/* Cards Row (Horizontally scrollable) */}
            <div
              ref={sliderRef}
              className="flex gap-5 overflow-x-auto pb-4 scroll-smooth no-scrollbar"
              style={{
                scrollbarWidth: "none",
                msOverflowStyle: "none",
              }}
            >
              {CASE_STUDIES.map((cs) => (
                <div
                  key={cs.id}
                  className="w-[290px] sm:w-[325px] shrink-0 rounded-[22px] bg-white border border-[#E2E8F0]/80 shadow-[0_4px_25px_rgba(0,0,0,0.04)] hover:shadow-xl transition-all duration-300 flex flex-col justify-between overflow-hidden group"
                >
                  {/* Top Image Banner with bottom fade */}
                  <div className={`relative h-[155px] w-full overflow-hidden ${cs.imageBg}`}>
                    <img
                      src={cs.image}
                      alt={cs.client}
                      className="h-full w-full object-cover object-center transition-transform duration-500 group-hover:scale-105"
                    />
                    {/* Gradient fade to white */}
                    <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-white to-transparent" />
                  </div>

                  {/* Card Title & Category */}
                  <div className="flex items-center justify-between px-5 pt-3.5 pb-2">
                    <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0B132B] whitespace-nowrap">
                      {cs.client}
                    </h3>
                    <div className="mx-3 h-4 w-[1px] bg-[#CBD5E1] shrink-0" />
                    <span className="text-[12px] font-medium leading-[15px] text-[#475569] text-right line-clamp-2">
                      {cs.category}
                    </span>
                  </div>

                  {/* Description Paragraph */}
                  <div className="px-5 pb-5 pt-1">
                    <p className="min-h-[66px] text-[13px] leading-[21px] text-[#64748B]">
                      {cs.desc}
                    </p>
                  </div>

                  {/* Bottom Metrics Pill Bar */}
                  <div className="mt-auto grid grid-cols-3 divide-x divide-white/15 bg-[#071B34] px-2.5 py-3.5 text-center rounded-b-[22px]">
                    {cs.metrics.map((m, mIdx) => (
                      <div key={mIdx} className="flex flex-col items-center px-1">
                        <span className="text-[15px] sm:text-[16px] font-bold text-white">
                          {m.value}
                        </span>
                        <span className="mt-0.5 text-[9.5px] sm:text-[10px] text-[#94A3B8] leading-tight text-center">
                          {m.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Slider Arrow Controls at bottom-right */}
            <div className="mt-4 flex items-center justify-end gap-3">
              <button
                type="button"
                onClick={() => scrollSlider("left")}
                aria-label="Previous Case Study"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#CBD5E1] bg-white text-[#475569] shadow-sm hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-all cursor-pointer"
              >
                <ArrowLeft className="h-4 w-4" strokeWidth={2} />
              </button>
              <button
                type="button"
                onClick={() => scrollSlider("right")}
                aria-label="Next Case Study"
                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#CBD5E1] bg-white text-[#475569] shadow-sm hover:border-[#94A3B8] hover:bg-[#F8FAFC] transition-all cursor-pointer"
              >
                <ArrowRight className="h-4 w-4" strokeWidth={2} />
              </button>
            </div>

          </div>

        </div>
      </div>
    </section>
  );
};

export default AiInActionSection;
